import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useDeferredValue,
  useRef,
  lazy,
  Suspense,
} from "react";
import { Link } from "react-router-dom";
import AdminHeader from "../components/Layout/AdminHeader";
import AdminSideBar from "../components/Admin/Layout/AdminSideBar";
import { FiDownload, FiChevronDown } from "react-icons/fi";
import { RxCross1 } from "react-icons/rx";
import axios from "axios";
import { server } from "../server";
import { toast } from "react-toastify";
import { exportToExcelWithRelated, exportToPDFWithRelated } from "../utils/exportUtils";
import Loader from "../components/Layout/Loader";
import DashboardTableScroll from "../components/shared/DashboardTableScroll";
import AdminPaginatedTable from "../components/shared/AdminPaginatedTable";
import { computeOrderVaBreakdown } from "../utils/vaOrderRevenue";
import {
  SalesMetricCard,
  RevenueDetailModal,
  renderMetricModalBody,
  METRIC_MODAL_TITLES,
} from "../components/Admin/AdminSalesMetrics";
import { TransactionDetailModal } from "../components/Admin/AdminTransactionDetail";
import {
  buildSalesYearOptions,
  formatSalesPeriodLabel,
  getDefaultPeriodAnchor,
  toDateInputValue,
} from "../utils/salesDateFilter";
const AdminSalesCharts = lazy(() => import("../components/Admin/AdminSalesCharts"));

const MAX_CHART_POINTS = 60;
const DEFAULT_TX_PAGE_SIZE = 10;
const DEFAULT_ESCROW_PAGE_SIZE = 10;

const EMPTY_SUMMARY = {
  totalSales: 0,
  totalOrders: 0,
  averageOrderValue: 0,
  refundedOrders: 0,
  refundedAmount: 0,
  totalItemsSold: 0,
  averageItemsPerOrder: 0,
  totalReturns: 0,
  totalReturnedItems: 0,
};

const EMPTY_VA_REVENUE = {
  buyerProtectionFees: 0,
  sellerPlatformFees: 0,
  featuredListingFees: 0,
  eventBannerFees: 0,
  serviceBannerFees: 0,
  promotionBannerFees: 0,
  promotionNet: 0,
  promotionStripeFees: 0,
  promotionPaymentCount: 0,
  vaNetEarnings: 0,
  stripeFeesEstimated: 0,
  vaPendingInEscrow: 0,
  vaReleased: 0,
};
const VALID_DATE_FILTERS = new Set(["all", "daily", "weekly", "monthly", "annually", "range"]);
const METRICS_WITHOUT_FETCH = new Set(["vaReleased", "customerEscrow"]);

const formatTxDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? "—" : dt.toLocaleString("en-GB");
};

const readInitialDateFilter = () => {
  const params = new URLSearchParams(window.location.search);
  const fromUrl = params.get("dateFilter");
  return fromUrl && VALID_DATE_FILTERS.has(fromUrl) ? fromUrl : "all";
};

const AdminDashboardTotalSales = () => {
  const [dateFilter, setDateFilter] = useState(readInitialDateFilter);
  const [periodAnchor, setPeriodAnchor] = useState(() => {
    const initial = readInitialDateFilter();
    return initial !== "all" && initial !== "range" ? getDefaultPeriodAnchor(initial) : "";
  });
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const salesYearOptions = useMemo(() => buildSalesYearOptions(15), []);
  const todayDateInput = useMemo(() => toDateInputValue(new Date()), []);
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const [selectedDay, setSelectedDay] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [escrowRows, setEscrowRows] = useState([]);
  const [escrowLoading, setEscrowLoading] = useState(true);
  const [escrowPage, setEscrowPage] = useState(1);
  const [escrowLimit, setEscrowLimit] = useState(DEFAULT_ESCROW_PAGE_SIZE);
  const [escrowPagination, setEscrowPagination] = useState({
    page: 1,
    limit: DEFAULT_ESCROW_PAGE_SIZE,
    total: 0,
    pages: 1,
  });
  const [selectedEscrowRow, setSelectedEscrowRow] = useState(null);
  const [isEscrowDetailsOpen, setIsEscrowDetailsOpen] = useState(false);
  const [feeSettings, setFeeSettings] = useState({
    platformFees: { enabled: true, percentage: 5 },
    stripeFees: { enabled: true, percentage: 2.9, fixedFee: 0.3 },
  });
  const [escrowSummary, setEscrowSummary] = useState({
    moneyInEscrow: 0,
    moneyInEscrowStripe: 0,
    moneyInEscrowWallet: 0,
    moneyInEscrowMixed: 0,
    totalWalletBalance: 0,
    escrowOrderCount: 0,
  });
  const [escrowVaSnapshot, setEscrowVaSnapshot] = useState({
    vaPendingInEscrow: 0,
    escrowOrderCount: 0,
    qualifyingOrderCount: 0,
  });
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [chartsLoading, setChartsLoading] = useState(true);
  const [dailySales, setDailySales] = useState([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [vaRevenue, setVaRevenue] = useState(EMPTY_VA_REVENUE);
  const [salesByCategory, setSalesByCategory] = useState([]);
  const [dayOrdersLoading, setDayOrdersLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [revenueDetailCounts, setRevenueDetailCounts] = useState({
    promotionPayments: 0,
    featuredPayments: 0,
    featuredAtCheckout: 0,
    eventBannerPayments: 0,
    serviceBannerPayments: 0,
    refundedOrders: 0,
  });
  const [escrowLoadEnabled, setEscrowLoadEnabled] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [transactionsTotal, setTransactionsTotal] = useState(0);
  const [txPage, setTxPage] = useState(1);
  const [txLimit, setTxLimit] = useState(DEFAULT_TX_PAGE_SIZE);
  const [txPagination, setTxPagination] = useState({
    page: 1,
    limit: DEFAULT_TX_PAGE_SIZE,
    total: 0,
    pages: 1,
  });
  const [txLoading, setTxLoading] = useState(false);
  const [dashboardMeta, setDashboardMeta] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [metricModalId, setMetricModalId] = useState(null);
  const [metricDetailRows, setMetricDetailRows] = useState([]);
  const [metricDetailFooter, setMetricDetailFooter] = useState(null);
  const [metricDetailLoading, setMetricDetailLoading] = useState(false);
  const [selectedCategoryDetails, setSelectedCategoryDetails] = useState(null);
  const [categoryDetailsLoading, setCategoryDetailsLoading] = useState(false);
  const escrowSectionRef = useRef(null);
  const debugEnabled =
    typeof window !== "undefined" &&
    window.localStorage.getItem("debugTotalSales") === "1";

  const debugLog = useCallback(
    (...args) => {
      if (debugEnabled) console.debug("[TotalSales]", ...args);
    },
    [debugEnabled]
  );

  const salesQueryParams = useMemo(() => {
    const params = { dateFilter };
    if (dateFilter === "range" && startDate && endDate) {
      params.startDate = startDate;
      params.endDate = endDate;
    } else if (dateFilter !== "all" && dateFilter !== "range" && periodAnchor) {
      params.startDate = periodAnchor;
    }
    return params;
  }, [dateFilter, periodAnchor, startDate, endDate]);

  const salesFilterKey = useMemo(
    () => JSON.stringify(salesQueryParams),
    [salesQueryParams]
  );

  const applyDashboardPayload = useCallback((data) => {
    setDailySales(Array.isArray(data?.dailySales) ? data.dailySales : []);
    setSummary({ ...EMPTY_SUMMARY, ...(data?.summary || {}) });
    setVaRevenue({ ...EMPTY_VA_REVENUE, ...(data?.vaRevenue || {}) });
    setSalesByCategory(Array.isArray(data?.salesByCategory) ? data.salesByCategory : []);
    setTransactions(Array.isArray(data?.transactions) ? data.transactions : []);
    setTransactionsTotal(Number(data?.transactionsTotal) || 0);
    if (data?.pagination) {
      setTxPagination({
        page: Number(data.pagination.page) || 1,
        limit: Number(data.pagination.limit) || txLimit,
        total: Number(data.pagination.total) || 0,
        pages: Number(data.pagination.pages) || 1,
      });
    }
    setDashboardMeta(data?.meta || null);
    setEscrowVaSnapshot({
      vaPendingInEscrow: Number(data?.escrowVaSnapshot?.vaPendingInEscrow || 0),
      escrowOrderCount: Number(data?.escrowVaSnapshot?.escrowOrderCount || 0),
      qualifyingOrderCount: Number(data?.escrowVaSnapshot?.qualifyingOrderCount || 0),
    });
    const counts = data?.revenueDetailCounts || data?.revenueDetails;
    if (counts && typeof counts.promotionPayments === "number") {
      setRevenueDetailCounts({
        promotionPayments: counts.promotionPayments || 0,
        featuredPayments: counts.featuredPayments || 0,
        featuredAtCheckout: counts.featuredAtCheckout || 0,
        eventBannerPayments: counts.eventBannerPayments || 0,
        serviceBannerPayments: counts.serviceBannerPayments || 0,
        refundedOrders: counts.refundedOrders || 0,
      });
    } else if (counts && Array.isArray(counts.eventBannerPayments)) {
      setRevenueDetailCounts({
        promotionPayments: counts.promotionPayments?.length || 0,
        featuredPayments: counts.featuredPayments?.length || 0,
        featuredAtCheckout: counts.featuredAtCheckout?.length || 0,
        eventBannerPayments: counts.eventBannerPayments?.length || 0,
        serviceBannerPayments: counts.serviceBannerPayments?.length || 0,
        refundedOrders: counts.refundedOrders?.length || 0,
      });
    }
    if (data?.feeSettings) {
      setFeeSettings({
        platformFees: data.feeSettings.platformFees || { enabled: true, percentage: 5 },
        stripeFees: data.feeSettings.stripeFees || {
          enabled: true,
          percentage: 2.9,
          fixedFee: 0.3,
        },
      });
    }
    setChartsLoading(false);
  }, []);

  useEffect(() => {
    if (dateFilter === "range" && (!startDate || !endDate)) {
      setDashboardLoading(false);
      setTxLoading(false);
      setChartsLoading(false);
      setDailySales([]);
      setSummary(EMPTY_SUMMARY);
      setVaRevenue(EMPTY_VA_REVENUE);
      setEscrowVaSnapshot({ vaPendingInEscrow: 0, escrowOrderCount: 0, qualifyingOrderCount: 0 });
      setSalesByCategory([]);
      setTransactions([]);
      setTransactionsTotal(0);
      setDashboardMeta(null);
      return undefined;
    }

    const controller = new AbortController();
    let active = true;

    (async () => {
      const isFirstPage = txPage === 1;
      const startedAt = performance.now();
      if (isFirstPage) {
        setDashboardLoading(true);
        setChartsLoading(true);
      } else {
        setTxLoading(true);
      }

      try {
        const url =
          txPage === 1
            ? `${server}/order/admin-sales-dashboard`
            : `${server}/order/admin-sales-transactions`;
        const { data } = await axios.get(url, {
          params: { ...salesQueryParams, txPage, txLimit },
          withCredentials: true,
          timeout: 120000,
          signal: controller.signal,
        });
        if (!active) return;
        if (isFirstPage) {
          applyDashboardPayload(data);
        } else {
          setTransactions(Array.isArray(data?.transactions) ? data.transactions : []);
          setTransactionsTotal(Number(data?.transactionsTotal) || 0);
          if (data?.pagination) {
            setTxPagination({
              page: Number(data.pagination.page) || txPage,
              limit: Number(data.pagination.limit) || txLimit,
              total: Number(data.pagination.total) || 0,
              pages: Number(data.pagination.pages) || 1,
            });
          }
          if (data?.meta) setDashboardMeta(data.meta);
        }
        debugLog(
          isFirstPage ? "dashboard loaded" : "transactions page loaded",
          {
            txPage,
            txLimit,
            durationMs: Math.round(performance.now() - startedAt),
            transactions: Array.isArray(data?.transactions) ? data.transactions.length : 0,
          }
        );
      } catch (err) {
        if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
        if (!active) return;
        toast.error(err?.response?.data?.message || "Failed to load sales data");
        if (isFirstPage) {
          setDailySales([]);
          setSummary(EMPTY_SUMMARY);
          setVaRevenue(EMPTY_VA_REVENUE);
          setSalesByCategory([]);
          setTransactions([]);
          setTransactionsTotal(0);
          setDashboardMeta(null);
          setChartsLoading(false);
        }
      } finally {
        if (!active) return;
        if (isFirstPage) setDashboardLoading(false);
        else setTxLoading(false);
      }
    })();

    return () => {
      active = false;
      controller.abort();
    };
  }, [salesFilterKey, txPage, txLimit, applyDashboardPayload, debugLog]);

  useEffect(() => {
    if (!escrowLoadEnabled) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const startedAt = performance.now();
        setEscrowLoading(true);
        const { data } = await axios.get(`${server}/wallet/admin/transactions`, {
          params: {
            escrowOnly: "1",
            escrowPage,
            escrowLimit,
          },
          withCredentials: true,
          timeout: 60000,
        });
        if (cancelled) return;
        setEscrowRows(Array.isArray(data?.escrowOrders) ? data.escrowOrders : []);
        if (data?.pagination) {
          setEscrowPagination({
            page: Number(data.pagination.page) || 1,
            limit: Number(data.pagination.limit) || escrowLimit,
            total: Number(data.pagination.total) || 0,
            pages: Number(data.pagination.pages) || 1,
          });
        }
        if (data?.summary) {
          setEscrowSummary({
            moneyInEscrow: Number(data.summary.moneyInEscrow || 0),
            moneyInEscrowStripe: Number(data.summary.moneyInEscrowStripe || 0),
            moneyInEscrowWallet: Number(data.summary.moneyInEscrowWallet || 0),
            moneyInEscrowMixed: Number(data.summary.moneyInEscrowMixed || 0),
            totalWalletBalance: Number(data.summary.totalWalletBalance || 0),
            escrowOrderCount: Number(data.summary.escrowOrderCount || 0),
          });
        }
        debugLog("escrow loaded", {
          escrowPage,
          escrowLimit,
          rows: Array.isArray(data?.escrowOrders) ? data.escrowOrders.length : 0,
          durationMs: Math.round(performance.now() - startedAt),
        });
      } catch (err) {
        if (!cancelled) {
          toast.error(err?.response?.data?.message || "Failed to load escrow data");
          setEscrowRows([]);
          setEscrowPagination({
            page: 1,
            limit: escrowLimit,
            total: 0,
            pages: 1,
          });
        }
      } finally {
        if (!cancelled) setEscrowLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [escrowLoadEnabled, escrowPage, escrowLimit, debugLog]);

  useEffect(() => {
    if (escrowLoadEnabled) return undefined;
    const node = escrowSectionRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setEscrowLoadEnabled(true);
            observer.disconnect();
            break;
          }
        }
      },
      { rootMargin: "240px 0px" }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [escrowLoadEnabled]);

  const platformFeePercent = Number(feeSettings?.platformFees?.percentage ?? 5);

  const openMetricModal = useCallback(
    async (id) => {
      setMetricModalId(id);
      if (METRICS_WITHOUT_FETCH.has(id)) {
        setMetricDetailRows([]);
        setMetricDetailFooter(null);
        setMetricDetailLoading(false);
        return;
      }
      setMetricDetailLoading(true);
      setMetricDetailRows([]);
      setMetricDetailFooter(null);
      try {
        const { data } = await axios.get(`${server}/order/admin-sales-metric-details`, {
          params: { metric: id, ...salesQueryParams },
          withCredentials: true,
          timeout: 120000,
        });
        setMetricDetailRows(Array.isArray(data?.rows) ? data.rows : []);
        setMetricDetailFooter(data?.footer || null);
      } catch (err) {
        toast.error(err?.response?.data?.message || "Failed to load metric details");
        setMetricDetailRows([]);
      } finally {
        setMetricDetailLoading(false);
      }
    },
    [salesQueryParams]
  );

  const closeMetricModal = useCallback(() => {
    setMetricModalId(null);
    setMetricDetailRows([]);
    setMetricDetailFooter(null);
    setMetricDetailLoading(false);
  }, []);

  const openCategoryDetails = useCallback(
    async (cat) => {
      setSelectedCategoryDetails({
        category: cat.category,
        itemsSold: cat.itemsSold,
        salesAmount: cat.salesAmount,
        itemBreakdown: [],
        lineItems: [],
      });
      setCategoryDetailsLoading(true);
      try {
        const { data } = await axios.get(`${server}/order/admin-sales-category-details`, {
          params: { category: cat.category, ...salesQueryParams },
          withCredentials: true,
          timeout: 120000,
        });
        setSelectedCategoryDetails({
          category: data.category || cat.category,
          itemsSold: data.itemsSold ?? cat.itemsSold,
          salesAmount: data.salesAmount ?? cat.salesAmount,
          itemBreakdown: Array.isArray(data.itemBreakdown) ? data.itemBreakdown : [],
          lineItems: Array.isArray(data.lineItems) ? data.lineItems : [],
        });
      } catch (err) {
        toast.error(err?.response?.data?.message || "Failed to load category details");
      } finally {
        setCategoryDetailsLoading(false);
      }
    },
    [salesQueryParams]
  );

  const closeCategoryDetails = useCallback(() => {
    setSelectedCategoryDetails(null);
    setCategoryDetailsLoading(false);
  }, []);

  const periodLabel = useMemo(
    () => formatSalesPeriodLabel(dateFilter, periodAnchor, startDate, endDate),
    [dateFilter, periodAnchor, startDate, endDate]
  );

  const metricModalCtx = useMemo(
    () => ({
      summary,
      vaRevenue,
      escrowSummary,
      revenueDetailCounts,
      dailySales,
      transactions,
      feeSettings,
      metricRows: metricDetailRows,
      metricFooter: metricDetailFooter,
      metricLoading: metricDetailLoading,
      metricTruncated: Boolean(dashboardMeta?.ordersTruncated),
      onTransactionSelect: setSelectedTransaction,
      periodLabel,
    }),
    [
      summary,
      vaRevenue,
      escrowSummary,
      revenueDetailCounts,
      dailySales,
      transactions,
      feeSettings,
      metricDetailRows,
      metricDetailFooter,
      metricDetailLoading,
      dashboardMeta?.ordersTruncated,
      periodLabel,
    ]
  );

  const vaNetExportColumns = useMemo(
    () => [
      { field: "date", headerName: "Date" },
      { field: "type", headerName: "Type" },
      { field: "reference", headerName: "Reference" },
      { field: "items", headerName: "Items" },
      { field: "buyer", headerName: "Buyer" },
      { field: "seller", headerName: "Seller" },
      { field: "buyerProtection", headerName: "Buyer protection" },
      { field: "sellerPlatformFee", headerName: "Seller platform fee" },
      { field: "featuredFee", headerName: "Featured fee" },
      { field: "stripeFeeEst", headerName: "Stripe fee (est.)" },
      { field: "vaNetEarnings", headerName: "VA net earnings (est.)" },
      { field: "orderTotal", headerName: "Order total" },
      { field: "payment", headerName: "Payment" },
      { field: "status", headerName: "Status" },
    ],
    []
  );

  const promotionExportColumns = useMemo(
    () => [
      { field: "date", headerName: "Date" },
      { field: "type", headerName: "Type" },
      { field: "name", headerName: "Event / service name" },
      { field: "amount", headerName: "Amount" },
      { field: "payment", headerName: "Payment method" },
      { field: "status", headerName: "Status" },
      { field: "note", headerName: "Note" },
      { field: "paymentIntentId", headerName: "Payment reference" },
    ],
    []
  );

  const transactionsExportColumns = useMemo(
    () => [
      { field: "date", headerName: "Date" },
      { field: "type", headerName: "Transaction type" },
      { field: "reference", headerName: "Reference" },
      { field: "paymentMethod", headerName: "Payment method" },
      { field: "grossAmount", headerName: "Gross amount" },
      { field: "vaEarnings", headerName: "VA net (est.)" },
      { field: "stripeFeeEst", headerName: "Stripe fee (est.)" },
      { field: "status", headerName: "Status" },
    ],
    []
  );

  const featuredExportColumns = useMemo(
    () => [
      { field: "date", headerName: "Date" },
      { field: "type", headerName: "Type" },
      { field: "name", headerName: "Product / order" },
      { field: "amount", headerName: "Amount" },
      { field: "payment", headerName: "Payment method" },
      { field: "stripeFeeEst", headerName: "Stripe fee (est.)" },
      { field: "source", headerName: "Source" },
      { field: "status", headerName: "Status" },
    ],
    []
  );

  const transactionColumns = useMemo(
    () => [
      {
        field: "date",
        headerName: "Date",
        minWidth: 150,
        renderCell: (row) => formatTxDate(row.date),
      },
      {
        field: "typeLabel",
        headerName: "Type",
        minWidth: 130,
      },
      {
        field: "reference",
        headerName: "Reference",
        minWidth: 120,
        renderCell: (row) => (
          <span className="font-medium text-slate-900">{row.reference}</span>
        ),
      },
      {
        field: "buyerName",
        headerName: "Buyer",
        minWidth: 120,
        renderCell: (row) => (
          <div>
            <p className="text-slate-800">{row.buyerName}</p>
            {row.buyerEmail ? (
              <p className="text-xs text-slate-500">{row.buyerEmail}</p>
            ) : null}
          </div>
        ),
      },
      {
        field: "sellerName",
        headerName: "Seller / paid by",
        minWidth: 140,
        renderCell: (row) => (
          <div>
            <p className="text-slate-800">{row.sellerName}</p>
            {row.sellerEmail ? (
              <p className="text-xs text-slate-500">{row.sellerEmail}</p>
            ) : null}
          </div>
        ),
      },
      {
        field: "grossAmount",
        headerName: "Gross",
        renderCell: (row) => (
          <span className="font-semibold">£{Number(row.grossAmount || 0).toFixed(2)}</span>
        ),
      },
      {
        field: "vaEarnings",
        headerName: "VA (est.)",
        renderCell: (row) => (
          <span className="text-emerald-800 font-medium">
            £{Number(row.vaEarnings || 0).toFixed(2)}
          </span>
        ),
      },
      {
        field: "paymentMethod",
        headerName: "Payment",
        minWidth: 90,
        renderCell: (row) => (
          <span className="capitalize text-slate-600">{row.paymentMethod || "—"}</span>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        minWidth: 100,
      },
    ],
    []
  );

  const filteredTransactions = useMemo(() => {
    const q = (deferredSearchQuery || "").trim().toLowerCase();
    if (!q) return transactions;
    return transactions.filter((t) => {
      const blob = [
        t.typeLabel,
        t.reference,
        t.buyerName,
        t.buyerEmail,
        t.sellerName,
        t.sellerEmail,
        t.paymentMethod,
        t.status,
        t.grossAmount,
        t.vaEarnings,
      ]
        .map((v) => String(v || "").toLowerCase())
        .join(" ");
      return blob.includes(q) || formatTxDate(t.date).toLowerCase().includes(q);
    });
  }, [transactions, deferredSearchQuery]);

  const columns = useMemo(() => [
    {
      field: "date",
      headerName: "Date",
      minWidth: 150,
      flex: 0.8,
      renderCell: (row) => (
        <span>{new Date(row.date).toLocaleDateString("en-GB", { 
          year: "numeric", 
          month: "short", 
          day: "numeric" 
        })}</span>
      ),
    },
    {
      field: "orderCount",
      headerName: "Valid Orders",
      type: "number",
      minWidth: 120,
      flex: 0.6,
    },
    {
      field: "totalItems",
      headerName: "Products Sold",
      type: "number",
      minWidth: 140,
      flex: 0.6,
    },
    {
      field: "totalOrders",
      headerName: "Total Orders",
      type: "number",
      minWidth: 120,
      flex: 0.6,
      renderCell: (row) => (
        <span className="text-slate-600">{row.totalOrders}</span>
      ),
    },
    {
      field: "totalSales",
      headerName: "Total Sales (customer paid)",
      renderCell: (row) => (
        <span className="font-semibold text-slate-900">
          £{Number(row.totalSales).toFixed(2)}
        </span>
      ),
    },
    {
      field: "totalSubtotal",
      headerName: "Subtotal (products)",
      renderCell: (row) => (
        <span className="text-slate-600">£{Number(row.totalSubtotal || 0).toFixed(2)}</span>
      ),
    },
    {
      field: "totalShipping",
      headerName: "Delivery (shipping)",
      renderCell: (row) => (
        <span className="text-slate-600">£{Number(row.totalShipping || 0).toFixed(2)}</span>
      ),
    },
    {
      field: "totalBuyerProtection",
      headerName: "Buyer protection (VA)",
      minWidth: 150,
      renderCell: (row) => (
        <span className="text-emerald-700 font-medium">
          £{Number(row.totalBuyerProtection || 0).toFixed(2)}
        </span>
      ),
    },
    {
      field: "totalSellerPlatformFees",
      headerName: `VA seller fee (${platformFeePercent}%)`,
      minWidth: 150,
      renderCell: (row) => (
        <span className="text-teal-700 font-medium">
          £{Number(row.totalSellerPlatformFees || 0).toFixed(2)}
        </span>
      ),
    },
    {
      field: "totalFeaturedFees",
      headerName: "Featured listing (VA)",
      minWidth: 140,
      renderCell: (row) => (
        <span className="text-slate-600">£{Number(row.totalFeaturedFees || 0).toFixed(2)}</span>
      ),
    },
    {
      field: "totalEventBannerFees",
      headerName: "Event banner fees",
      minWidth: 140,
      renderCell: (row) => (
        <span className="text-slate-600">£{Number(row.totalEventBannerFees || 0).toFixed(2)}</span>
      ),
    },
    {
      field: "totalServiceBannerFees",
      headerName: "Service banner fees",
      minWidth: 140,
      renderCell: (row) => (
        <span className="text-slate-600">£{Number(row.totalServiceBannerFees || 0).toFixed(2)}</span>
      ),
    },
    {
      field: "totalVaNet",
      headerName: "VA net earnings (est.)",
      minWidth: 160,
      renderCell: (row) => (
        <span className="font-semibold text-emerald-800">
          £{Number(row.totalVaNet || 0).toFixed(2)}
        </span>
      ),
    },
    {
      field: "totalStripeFees",
      headerName: "Stripe fees (est.)",
      minWidth: 140,
      renderCell: (row) => (
        <span className="text-slate-600">£{Number(row.totalStripeFees || 0).toFixed(2)}</span>
      ),
    },
    {
      field: "totalDiscount",
      headerName: "Discount",
      minWidth: 100,
      renderCell: (row) => (
        <span className="text-slate-600">£{Number(row.totalDiscount || 0).toFixed(2)}</span>
      ),
    },
    {
      field: "averageOrderValue",
      headerName: "Avg Order Value",
      renderCell: (row) => (
        <span className="text-slate-600">£{Number(row.averageOrderValue).toFixed(2)}</span>
      ),
    },
  ], [platformFeePercent]);

  const rows = useMemo(
    () =>
      dailySales.map((day, index) => ({
        id: `sales-${day.date}-${index}`,
        date: day.date,
        orderCount: day.orderCount,
        totalItems: day.totalItems ?? 0,
        totalSales: day.totalSales,
        totalSubtotal: day.totalSubtotal ?? 0,
        totalShipping: day.totalShipping ?? 0,
        totalBuyerProtection: day.totalBuyerProtection ?? 0,
        totalSellerPlatformFees: day.totalSellerPlatformFees ?? 0,
        totalFeaturedFees: day.totalFeaturedFees ?? 0,
        totalEventBannerFees: day.totalEventBannerFees ?? 0,
        totalServiceBannerFees: day.totalServiceBannerFees ?? 0,
        totalPromotionFees: day.totalPromotionFees ?? 0,
        totalVaNet: day.totalVaNet ?? 0,
        totalStripeFees: day.totalStripeFees ?? 0,
        totalDiscount: day.totalDiscount ?? 0,
        averageOrderValue: day.orderCount > 0 ? day.totalSales / day.orderCount : 0,
        totalOrders: day.totalOrders ?? 0,
      })),
    [dailySales]
  );

  const handleSalesRowClick = useCallback(async (row) => {
    setSelectedDay({ ...row, orders: [] });
    setIsDetailsOpen(true);
    setDayOrdersLoading(true);
    try {
      const { data } = await axios.get(`${server}/order/admin-sales-day/${row.date}`, {
        withCredentials: true,
        timeout: 60000,
      });
      setSelectedDay((prev) =>
        prev && prev.date === row.date
          ? { ...prev, orders: Array.isArray(data?.orders) ? data.orders : [] }
          : prev
      );
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load orders for this day");
    } finally {
      setDayOrdersLoading(false);
    }
  }, []);

  const escrowPageNumbers = useMemo(() => {
    const totalPages = Math.max(1, Number(escrowPagination.pages) || 1);
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, idx) => idx + 1);
    }
    const current = Math.min(totalPages, Math.max(1, Number(escrowPage) || 1));
    let start = Math.max(1, current - 2);
    let end = Math.min(totalPages, start + 4);
    start = Math.max(1, end - 4);
    const pages = [];
    if (start > 1) pages.push(1);
    if (start > 2) pages.push("…");
    for (let p = start; p <= end; p += 1) pages.push(p);
    if (end < totalPages - 1) pages.push("…");
    if (end < totalPages) pages.push(totalPages);
    return pages;
  }, [escrowPagination.pages, escrowPage]);

  const escrowColumns = useMemo(() => [
    {
      field: "createdAt",
      headerName: "Date",
      minWidth: 150,
      flex: 0.7,
      renderCell: (row) =>
        row.createdAt ? new Date(row.createdAt).toLocaleDateString("en-GB") : "—",
    },
    {
      field: "orderId",
      headerName: "Order ID",
      renderCell: (row) => (row.orderId ? `#${row.orderId}` : "—"),
    },
    { field: "sellerName", headerName: "Seller" },
    { field: "sellerEmail", headerName: "Seller Email" },
    { field: "customerName", headerName: "Customer" },
    {
      field: "paymentMethod",
      headerName: "Paid via",
      renderCell: (row) => {
        const method = String(row.paymentMethod || "stripe").toLowerCase();
        const label =
          method === "wallet"
            ? "Wallet"
            : method === "mixed"
            ? "Wallet + card"
            : method === "stripe"
            ? "Stripe"
            : method;
        const cls =
          method === "wallet"
            ? "bg-violet-100 text-violet-800"
            : method === "mixed"
            ? "bg-indigo-100 text-indigo-800"
            : "bg-sky-100 text-sky-800";
        return (
          <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ${cls}`}>
            {label}
          </span>
        );
      },
    },
    {
      field: "amount",
      headerName: "Escrow Amount",
      renderCell: (row) => `£${Number(row.amount || 0).toFixed(2)}`,
    },
    {
      field: "paymentStatus",
      headerName: "Payment",
      renderCell: (row) => (
        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-700">
          {row.paymentStatus || "Succeeded"}
        </span>
      ),
    },
  ], []);

  const orderBreakdownColumns = useMemo(
    () => [
      { field: "orderId", headerName: "Order ID" },
      { field: "date", headerName: "Date" },
      { field: "subtotal", headerName: "Subtotal (products)" },
      { field: "delivery", headerName: "Delivery (shipping)" },
      { field: "vaBuyerProtection", headerName: "Buyer protection (VA)" },
      {
        field: "vaSellerFee",
        headerName: `VA seller fee (${platformFeePercent}%)`,
      },
      { field: "vaFeaturedFee", headerName: "Featured listing (VA)" },
      { field: "vaNetEarnings", headerName: "VA net earnings (est.)" },
      { field: "vaStripeFeeEst", headerName: "Stripe fee (est.)" },
      { field: "paymentMethod", headerName: "Payment method" },
      { field: "discount", headerName: "Discount" },
      { field: "total", headerName: "Total (customer paid)" },
      { field: "status", headerName: "Status" },
    ],
    [platformFeePercent]
  );

  const returnsExportColumns = [
    { field: "orderId", headerName: "Order ID" },
    { field: "date", headerName: "Date" },
    { field: "type", headerName: "Return Type" },
    { field: "productName", headerName: "Product" },
    { field: "quantity", headerName: "Quantity" },
    { field: "amount", headerName: "Refund Amount" },
    { field: "status", headerName: "Status" },
  ];

  const exportFilename = `Total_Sales_${dateFilter}_${periodAnchor || startDate || "all"}_${new Date().toISOString().split("T")[0]}`;
  const exportPageInfo = {
    title: `Total Sales Report - ${periodLabel}`,
    description: `Sales by day with VA fee breakdown (buyer protection, seller platform fee, featured listing fees, event/service banner fees, Stripe fees, net VA earnings). Includes marketplace orders, events, services, and featured products with wallet vs Stripe payment method.`,
  };

  const salesChartData = useMemo(() => {
    const points = dailySales
      .map((day) => ({
        date: new Date(day.date).toLocaleDateString("en-GB", {
          month: "short",
          day: "numeric",
        }),
        sales: day.totalSales,
        orders: day.orderCount,
        sortKey: day.date,
      }))
      .sort((a, b) => String(a.sortKey).localeCompare(String(b.sortKey)));
    if (points.length <= MAX_CHART_POINTS) return points;
    return points.slice(-MAX_CHART_POINTS);
  }, [dailySales]);

  const showSalesCharts = !chartsLoading && salesChartData.length > 0;

  const goToTxPage = (nextPage) => {
    const page = Math.max(1, Math.min(txPagination.pages || 1, nextPage));
    if (page === txPage) return;
    setTxPage(page);
  };

  const txPageNumbers = useMemo(() => {
    const totalPages = Math.max(1, Number(txPagination.pages) || 1);
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, idx) => idx + 1);
    }
    const current = Math.min(totalPages, Math.max(1, Number(txPage) || 1));
    let start = Math.max(1, current - 2);
    let end = Math.min(totalPages, start + 4);
    start = Math.max(1, end - 4);
    const pages = [];
    if (start > 1) pages.push(1);
    if (start > 2) pages.push("…");
    for (let p = start; p <= end; p += 1) pages.push(p);
    if (end < totalPages - 1) pages.push("…");
    if (end < totalPages) pages.push(totalPages);
    return pages;
  }, [txPagination.pages, txPage]);

  const fetchExportPayload = useCallback(async () => {
    const { data } = await axios.get(`${server}/order/admin-sales-export`, {
      params: salesQueryParams,
      withCredentials: true,
      timeout: 120000,
    });
    const exportRows = (data?.dailySales || []).map((day, index) => ({
      id: `sales-${day.date}-${index}`,
      date: day.date,
      orderCount: day.orderCount,
      totalItems: day.totalItems ?? 0,
      totalSales: day.totalSales,
      totalSubtotal: day.totalSubtotal ?? 0,
      totalShipping: day.totalShipping ?? 0,
      totalBuyerProtection: day.totalBuyerProtection ?? 0,
      totalSellerPlatformFees: day.totalSellerPlatformFees ?? 0,
      totalFeaturedFees: day.totalFeaturedFees ?? 0,
      totalEventBannerFees: day.totalEventBannerFees ?? 0,
      totalServiceBannerFees: day.totalServiceBannerFees ?? 0,
      totalPromotionFees: day.totalPromotionFees ?? 0,
      totalVaNet: day.totalVaNet ?? 0,
      totalStripeFees: day.totalStripeFees ?? 0,
      totalDiscount: day.totalDiscount ?? 0,
      averageOrderValue: day.orderCount > 0 ? day.totalSales / day.orderCount : 0,
      totalOrders: day.totalOrders ?? 0,
    }));
    return {
      exportRows,
      orderBreakdown: data?.orderBreakdown || [],
      returns: data?.returns || [],
      categoryRows: data?.salesByCategory || [],
      vaNetTransactions: data?.vaNetTransactions || [],
      transactions: data?.transactions || [],
      eventBannerPayments: data?.eventBannerPayments || [],
      serviceBannerPayments: data?.serviceBannerPayments || [],
      featuredListingPayments: data?.featuredListingPayments || [],
    };
  }, [salesQueryParams]);

  const handleExportExcel = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const {
        exportRows,
        orderBreakdown,
        returns,
        categoryRows,
        vaNetTransactions,
        transactions,
        eventBannerPayments,
        serviceBannerPayments,
        featuredListingPayments,
      } = await fetchExportPayload();
      exportToExcelWithRelated(
        exportRows,
        columns,
        [
          {
            name: "Order breakdown",
            rows: orderBreakdown,
            columns: orderBreakdownColumns,
          },
          {
            name: "Transactions",
            rows: transactions,
            columns: transactionsExportColumns,
          },
          {
            name: "Event banner payments",
            rows: eventBannerPayments,
            columns: promotionExportColumns,
          },
          {
            name: "Service banner payments",
            rows: serviceBannerPayments,
            columns: promotionExportColumns,
          },
          {
            name: "Featured listing payments",
            rows: featuredListingPayments,
            columns: featuredExportColumns,
          },
          {
            name: "VA net earnings",
            rows: vaNetTransactions,
            columns: vaNetExportColumns,
          },
          {
            name: "Sales by category",
            rows: categoryRows,
            columns: [
              { field: "category", headerName: "Category" },
              { field: "itemsSold", headerName: "Products Sold" },
              { field: "salesAmount", headerName: "Sales Amount" },
            ],
          },
          {
            name: "Returns",
            rows: returns,
            columns: returnsExportColumns,
          },
        ],
        exportFilename,
        exportPageInfo
      );
      toast.success("Excel file exported successfully");
    } catch (error) {
      toast.error("Failed to export Excel file: " + (error.message || "Unknown error"));
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const {
        exportRows,
        orderBreakdown,
        returns,
        categoryRows,
        vaNetTransactions,
        transactions,
        eventBannerPayments,
        serviceBannerPayments,
        featuredListingPayments,
      } = await fetchExportPayload();
      exportToPDFWithRelated(
        exportRows,
        columns,
        [
          {
            name: "Order breakdown",
            rows: orderBreakdown,
            columns: orderBreakdownColumns,
          },
          {
            name: "Transactions",
            rows: transactions,
            columns: transactionsExportColumns,
          },
          {
            name: "Event banner payments",
            rows: eventBannerPayments,
            columns: promotionExportColumns,
          },
          {
            name: "Service banner payments",
            rows: serviceBannerPayments,
            columns: promotionExportColumns,
          },
          {
            name: "Featured listing payments",
            rows: featuredListingPayments,
            columns: featuredExportColumns,
          },
          {
            name: "VA net earnings",
            rows: vaNetTransactions,
            columns: vaNetExportColumns,
          },
          {
            name: "Sales by category",
            rows: categoryRows,
            columns: [
              { field: "category", headerName: "Category" },
              { field: "itemsSold", headerName: "Products Sold" },
              { field: "salesAmount", headerName: "Sales Amount" },
            ],
          },
          {
            name: "Returns",
            rows: returns,
            columns: returnsExportColumns,
          },
        ],
        exportFilename,
        {
          ...exportPageInfo,
          description: exportPageInfo.description + ` ${exportRows.length} day(s) in period.`,
        }
      );
      toast.success("PDF file exported successfully");
    } catch (error) {
      toast.error("Failed to export PDF file: " + (error.message || "Unknown error"));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="admin-dashboard-page min-h-screen">
      <AdminHeader />
      <div className="max-w-[1500px] mx-auto px-4 lg:px-6 py-6 flex flex-col lg:flex-row lg:items-start gap-6">
        <div className="w-full lg:w-[300px] shrink-0">
          <AdminSideBar active={10} />
        </div>

        <div className="min-w-0 flex-1 w-full">
          <div className="w-full min-w-0 rounded-2xl border border-slate-200 bg-white shadow-sm relative overflow-x-hidden">
            {dashboardLoading && (
              <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/80">
                <Loader />
              </div>
            )}
            <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white flex flex-wrap items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Total Sales</h2>
                <p className="text-sm text-slate-600 mt-1">
                  Period: <strong>{periodLabel}</strong> — marketplace orders, events, services, and
                  featured payments
                </p>
                {dashboardMeta?.ordersTruncated ? (
                  <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mt-2 max-w-3xl">
                    Summary metrics use the most recent{" "}
                    <strong>{dashboardMeta.ordersProcessed?.toLocaleString()}</strong> of{" "}
                    <strong>{dashboardMeta.ordersTotal?.toLocaleString()}</strong> orders in this
                    period. Narrow the date filter for exact totals on very large histories.
                  </p>
                ) : null}
              </div>
              <div className="flex flex-wrap items-center gap-3 justify-end">
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-slate-700">Filter by:</label>
                    <select
                      value={dateFilter}
                      onChange={(e) => {
                        const next = e.target.value;
                        setTxPage(1);
                        setDateFilter(next);
                        if (next === "range") {
                          setPeriodAnchor("");
                        } else if (next === "all") {
                          setPeriodAnchor("");
                          setStartDate("");
                          setEndDate("");
                        } else {
                          setPeriodAnchor(getDefaultPeriodAnchor(next));
                          setStartDate("");
                          setEndDate("");
                        }
                      }}
                      className="px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#38513b] focus:border-transparent"
                    >
                      <option value="all">All Time</option>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="annually">Yearly</option>
                      <option value="range">Custom Range</option>
                    </select>
                  </div>
                  {dateFilter === "daily" && (
                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium text-slate-700">Day:</label>
                      <input
                        type="date"
                        value={periodAnchor}
                        max={todayDateInput}
                        onChange={(e) => {
                          setTxPage(1);
                          setPeriodAnchor(e.target.value);
                        }}
                        className="px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#38513b] focus:border-transparent"
                      />
                    </div>
                  )}
                  {dateFilter === "weekly" && (
                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium text-slate-700">Week:</label>
                      <input
                        type="week"
                        value={periodAnchor}
                        onChange={(e) => {
                          setTxPage(1);
                          setPeriodAnchor(e.target.value);
                        }}
                        className="px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#38513b] focus:border-transparent"
                      />
                    </div>
                  )}
                  {dateFilter === "monthly" && (
                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium text-slate-700">Month:</label>
                      <input
                        type="month"
                        value={periodAnchor}
                        max={todayDateInput.slice(0, 7)}
                        onChange={(e) => {
                          setTxPage(1);
                          setPeriodAnchor(e.target.value);
                        }}
                        className="px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#38513b] focus:border-transparent"
                      />
                    </div>
                  )}
                  {dateFilter === "annually" && (
                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium text-slate-700">Year:</label>
                      <select
                        value={periodAnchor}
                        onChange={(e) => {
                          setTxPage(1);
                          setPeriodAnchor(e.target.value);
                        }}
                        className="px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#38513b] focus:border-transparent"
                      >
                        {salesYearOptions.map((year) => (
                          <option key={year} value={String(year)}>
                            {year}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {dateFilter === "range" && (
                    <div className="flex items-center gap-2">
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => {
                          setTxPage(1);
                          setStartDate(e.target.value);
                        }}
                        className="px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#38513b] focus:border-transparent"
                      />
                      <span className="text-sm text-slate-600">to</span>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => {
                          setTxPage(1);
                          setEndDate(e.target.value);
                        }}
                        min={startDate}
                        className="px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#38513b] focus:border-transparent"
                      />
                    </div>
                  )}
                  <div className="relative">
                    <button
                      onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
                      className="px-4 py-2.5 rounded-lg border border-slate-300 text-slate-700 font-medium hover:bg-slate-50 transition flex items-center gap-2"
                      title="Export data"
                    >
                      <FiDownload size={18} />
                      <span className="hidden sm:inline">Export</span>
                      <FiChevronDown size={16} className={`transition-transform ${exportDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {exportDropdownOpen && (
                      <>
                        <div 
                          className="fixed inset-0 z-10" 
                          onClick={() => setExportDropdownOpen(false)}
                        />
                        <div className="absolute left-0 mt-2 w-full bg-white rounded-lg shadow-lg border border-slate-200 z-20 overflow-hidden">
                          <button
                            onClick={() => {
                              setExportDropdownOpen(false);
                              handleExportExcel();
                            }}
                            className="w-full px-4 py-2.5 text-left text-slate-700 hover:bg-slate-50 transition flex items-center gap-2"
                          >
                            <FiDownload size={16} />
                            <span>Excel</span>
                          </button>
                          <button
                            onClick={() => {
                              setExportDropdownOpen(false);
                              handleExportPDF();
                            }}
                            className="w-full px-4 py-2.5 text-left text-slate-700 hover:bg-slate-50 transition flex items-center gap-2 border-t border-slate-200"
                          >
                            <FiDownload size={16} />
                            <span>PDF</span>
                          </button>
                        </div>
                      </>
                    )}
                  </div>
              </div>
            </div>

            <div className="px-6 py-4 border-b border-slate-200">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
                <SalesMetricCard
                  tone="blue"
                  label="Total Sales"
                  value={`£${summary.totalSales.toFixed(2)}`}
                  onClick={() => openMetricModal("totalSales")}
                />
                <SalesMetricCard
                  tone="green"
                  label="Total Orders"
                  value={summary.totalOrders}
                  onClick={() => openMetricModal("totalOrders")}
                />
                <SalesMetricCard
                  tone="purple"
                  label="Avg Order Value"
                  value={`£${summary.averageOrderValue.toFixed(2)}`}
                  onClick={() => openMetricModal("avgOrderValue")}
                />
                <SalesMetricCard
                  tone="red"
                  label="Refunded"
                  value={summary.refundedOrders}
                  subValue={`£${summary.refundedAmount.toFixed(2)}`}
                  onClick={() => openMetricModal("refunded")}
                />
                <SalesMetricCard
                  tone="amber"
                  label="Products Sold"
                  value={summary.totalItemsSold}
                  subValue={`Avg items/order: ${summary.averageItemsPerOrder.toFixed(2)}`}
                  onClick={() => openMetricModal("productsSold")}
                />
                <SalesMetricCard
                  tone="cyan"
                  label="Returns"
                  value={summary.totalReturns}
                  subValue={`Returned items: ${summary.totalReturnedItems}`}
                  onClick={() => openMetricModal("returns")}
                />
              </div>

              {/* VA platform revenue & fund holding */}
              <div className="mb-6 space-y-4">
                <div>
                  <h3 className="text-base font-semibold text-slate-900">VA platform revenue</h3>
                  <p className="text-xs text-slate-500 mt-1 max-w-3xl">
                    Income from marketplace order fees (buyer protection, seller platform fee, featured listings) plus
                    event and service banner advertising payments. Net VA earnings subtract estimated Stripe processing
                    on card payments. Wallet checkout for orders is held in escrow until the buyer confirms delivery — it
                    does not appear in Stripe. Wallet-funded banner payments were collected when the seller topped up their
                    wallet.
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <SalesMetricCard
                    tone="emerald"
                    label="Buyer protection fees"
                    value={`£${vaRevenue.buyerProtectionFees.toFixed(2)}`}
                    className="!text-xl"
                    onClick={() => openMetricModal("buyerProtection")}
                  />
                  <SalesMetricCard
                    tone="teal"
                    label={`Seller platform fees (${feeSettings.platformFees?.percentage || 0}%)`}
                    value={`£${vaRevenue.sellerPlatformFees.toFixed(2)}`}
                    className="[&_p:nth-child(2)]:text-xl"
                    onClick={() => openMetricModal("sellerPlatform")}
                  />
                  <SalesMetricCard
                    tone="purple"
                    label="Featured listing fees"
                    value={`£${vaRevenue.featuredListingFees.toFixed(2)}`}
                    className="[&_p:nth-child(2)]:text-xl"
                    onClick={() => openMetricModal("featuredListing")}
                  />
                  <SalesMetricCard
                    tone="indigo"
                    label="Event banner fees"
                    value={`£${Number(vaRevenue.eventBannerFees || 0).toFixed(2)}`}
                    subValue={
                      revenueDetailCounts.eventBannerPayments > 0
                        ? `${revenueDetailCounts.eventBannerPayments} payment(s) — tap for list`
                        : undefined
                    }
                    className="[&_p:nth-child(2)]:text-xl"
                    onClick={() => openMetricModal("eventBanner")}
                  />
                  <SalesMetricCard
                    tone="violet"
                    label="Service banner fees"
                    value={`£${Number(vaRevenue.serviceBannerFees || 0).toFixed(2)}`}
                    subValue={
                      revenueDetailCounts.serviceBannerPayments > 0
                        ? `${revenueDetailCounts.serviceBannerPayments} payment(s) — tap for list`
                        : undefined
                    }
                    className="[&_p:nth-child(2)]:text-xl"
                    onClick={() => openMetricModal("serviceBanner")}
                  />
                  <SalesMetricCard
                    tone="slate"
                    label="VA net earnings (est.)"
                    value={`£${vaRevenue.vaNetEarnings.toFixed(2)}`}
                    subValue={`Banner gross £${Number(vaRevenue.promotionBannerFees || 0).toFixed(2)} · Stripe est. £${vaRevenue.stripeFeesEstimated.toFixed(2)}`}
                    className="lg:col-span-2 [&_p:nth-child(2)]:text-xl"
                    onClick={() => openMetricModal("vaNet")}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <SalesMetricCard
                    tone="amber"
                    label="VA pending (in escrow)"
                    value={`£${Number(escrowVaSnapshot.vaPendingInEscrow || 0).toFixed(2)}`}
                    subValue={
                      escrowVaSnapshot.escrowOrderCount > 0
                        ? `${escrowVaSnapshot.escrowOrderCount} open order(s) — tap for VA breakdown`
                        : "No open orders in escrow"
                    }
                    className="[&_p:nth-child(2)]:text-lg"
                    onClick={() => openMetricModal("vaPendingEscrow")}
                  />
                  <SalesMetricCard
                    tone="green"
                    label="VA released to sellers"
                    value={`£${vaRevenue.vaReleased.toFixed(2)}`}
                    className="[&_p:nth-child(2)]:text-lg"
                    onClick={() => openMetricModal("vaReleased")}
                  />
                  <SalesMetricCard
                    tone="sky"
                    label="Customer funds held in escrow"
                    value={`£${escrowSummary.moneyInEscrow.toFixed(2)}`}
                    subValue={`${escrowSummary.escrowOrderCount} orders · Stripe £${escrowSummary.moneyInEscrowStripe.toFixed(2)} · Wallet £${escrowSummary.moneyInEscrowWallet.toFixed(2)}`}
                    className="lg:col-span-2 [&_p:nth-child(2)]:text-lg"
                    onClick={() => openMetricModal("customerEscrow")}
                  />
                </div>
                <p className="text-xs text-slate-500">
                  Seller wallet balances (available to withdraw): £
                  {escrowSummary.totalWalletBalance.toFixed(2)}
                </p>
              </div>

            </div>

            {chartsLoading ? (
              <div className="px-6 py-8 border-b border-slate-200">
                <div className="h-8 w-48 bg-slate-100 rounded animate-pulse mb-4" />
                <div className="grid min-w-0 gap-6 lg:grid-cols-2">
                  <div className="h-[300px] rounded-lg border border-slate-200 bg-slate-50 animate-pulse" />
                  <div className="h-[300px] rounded-lg border border-slate-200 bg-slate-50 animate-pulse" />
                </div>
              </div>
            ) : showSalesCharts ? (
              <Suspense
                fallback={
                  <div className="px-6 py-8 text-center text-sm text-slate-500">Loading charts…</div>
                }
              >
                <AdminSalesCharts chartData={salesChartData} maxChartPoints={MAX_CHART_POINTS} />
              </Suspense>
            ) : null}

            <div
              className="px-6 pt-2 pb-1"
              style={{ contentVisibility: "auto", containIntrinsicSize: "0 320px" }}
            >
              <h3 className="text-lg font-semibold text-slate-900">Transactions</h3>
              <p className="text-xs text-slate-500 mt-1 max-w-4xl">
                Marketplace checkouts, event and service banner payments, and featured listing fees for
                the period selected above ({periodLabel}). {txLimit} rows per page — click a row
                for full details.
              </p>
            </div>

            <div className="px-6 pt-4">
              <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2">
                  <label className="text-xs text-slate-600 whitespace-nowrap">Rows:</label>
                  <select
                    value={txLimit}
                    onChange={(e) => {
                      const next = Number(e.target.value) || 10;
                      setTxPage(1);
                      setTxLimit(next);
                    }}
                    className="rounded border border-slate-300 px-2 py-2 text-sm"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search buyer, seller, reference, amount…"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-[#38513b] focus:outline-none focus:ring-2 focus:ring-[#38513b]/20 sm:w-72"
                />
              </div>
            </div>

            <div className="p-4 pt-0">
              {dashboardLoading || txLoading ? (
                <div className="flex justify-center items-center h-64">
                  <Loader />
                </div>
              ) : (
                <>
                  <DashboardTableScroll hintText="Scroll sideways for all columns.">
                    <AdminPaginatedTable
                      columns={transactionColumns}
                      rows={filteredTransactions}
                      serverPaged
                      onRowClick={(row) => setSelectedTransaction(row)}
                      emptyMessage="No transactions for this period"
                    />
                  </DashboardTableScroll>
                  {transactionsTotal > 0 ? (
                    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-600">
                      <span>
                        Page {txPagination.page} of {txPagination.pages} ·{" "}
                        {transactionsTotal.toLocaleString()} total
                        {searchQuery.trim()
                          ? ` · ${filteredTransactions.length} match on this page`
                          : ""}
                      </span>
                      <div className="flex gap-1 flex-wrap">
                        {txPageNumbers.map((p, idx) =>
                          p === "…" ? (
                            <span
                              key={`ellipsis-${idx}`}
                              className="px-2 py-1 text-slate-400 select-none"
                            >
                              …
                            </span>
                          ) : (
                            <button
                              key={`tx-page-${p}`}
                              type="button"
                              disabled={txLoading || dashboardLoading || p === txPage}
                              onClick={() => goToTxPage(p)}
                              className={`px-3 py-1.5 rounded-md border text-sm ${
                                p === txPage
                                  ? "border-[#38513b] bg-[#38513b] text-white"
                                  : "border-slate-300 bg-white hover:bg-slate-50 text-slate-700"
                              } disabled:opacity-60`}
                            >
                              {p}
                            </button>
                          )
                        )}
                      </div>
                    </div>
                  ) : null}
                </>
              )}
            </div>

            {/* Sales by Category */}
            {salesByCategory.length > 0 && (
              <div className="px-6 pb-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Sales by Category</h3>
                <p className="text-xs text-slate-500 mb-2">
                  Click a category row to view products sold and order line items.
                </p>
                <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-2 text-left font-semibold text-slate-700">Category</th>
                        <th className="px-4 py-2 text-left font-semibold text-slate-700">Products Sold</th>
                        <th className="px-4 py-2 text-left font-semibold text-slate-700">Sales Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salesByCategory.map((cat) => (
                        <tr
                          key={cat.category}
                          className="border-t border-slate-200 cursor-pointer hover:bg-slate-50"
                          onClick={() => openCategoryDetails(cat)}
                        >
                          <td className="px-4 py-2 text-slate-800">{cat.category}</td>
                          <td className="px-4 py-2 text-slate-600">{cat.itemsSold}</td>
                          <td className="px-4 py-2 text-slate-600">£{cat.salesAmount.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Escrow Table */}
            <div
              ref={escrowSectionRef}
              className="px-6 pb-6"
              style={{ contentVisibility: "auto", containIntrinsicSize: "0 280px" }}
            >
              <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Escrow Orders</h3>
                  <p className="text-xs text-slate-500 mt-1">
                    Paid orders awaiting buyer confirmation. Includes wallet and card — use &quot;Paid via&quot; to see funding source.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs text-slate-600">Rows:</label>
                  <select
                    value={escrowLimit}
                    onChange={(e) => {
                      const next = Number(e.target.value) || 10;
                      setEscrowPage(1);
                      setEscrowLimit(next);
                    }}
                    className="rounded border border-slate-300 px-2 py-1 text-sm"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>

              {!escrowLoadEnabled ? (
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-600">
                  Escrow rows will load when this section is in view.
                </div>
              ) : escrowLoading ? (
                <div className="flex justify-center items-center h-40">
                  <Loader />
                </div>
              ) : (
                <DashboardTableScroll>
                  <AdminPaginatedTable
                    columns={escrowColumns}
                    rows={escrowRows}
                    serverPaged
                    onRowClick={(row) => {
                      setSelectedEscrowRow(row);
                      setIsEscrowDetailsOpen(true);
                    }}
                    emptyMessage="No orders in escrow"
                  />
                </DashboardTableScroll>
              )}
              {!escrowLoading && escrowPagination.total > 0 ? (
                <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-600">
                  <span>
                    Page {escrowPagination.page} of {escrowPagination.pages} ·{" "}
                    {escrowPagination.total.toLocaleString()} total
                  </span>
                  <div className="flex gap-1 flex-wrap">
                    {escrowPageNumbers.map((p, idx) =>
                      p === "…" ? (
                        <span key={`escrow-ellipsis-${idx}`} className="px-2 py-1 text-slate-400">
                          …
                        </span>
                      ) : (
                        <button
                          key={`escrow-page-${p}`}
                          type="button"
                          disabled={escrowLoading || p === escrowPage}
                          onClick={() => setEscrowPage(p)}
                          className={`px-3 py-1.5 rounded-md border text-sm ${
                            p === escrowPage
                              ? "border-[#38513b] bg-[#38513b] text-white"
                              : "border-slate-300 bg-white hover:bg-slate-50 text-slate-700"
                          } disabled:opacity-60`}
                        >
                          {p}
                        </button>
                      )
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* Day Details Modal */}
      {isDetailsOpen && selectedDay && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsDetailsOpen(false);
            }
          }}
        >
          <div 
            className="bg-white rounded-2xl shadow-xl w-[95%] max-w-4xl max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
              <h3 className="text-xl font-semibold text-slate-900">
                Sales Details - {new Date(selectedDay.date).toLocaleDateString("en-GB", { 
                  year: "numeric", 
                  month: "long", 
                  day: "numeric" 
                })}
              </h3>
              <button
                type="button"
                onClick={() => setIsDetailsOpen(false)}
                className="p-2 hover:bg-slate-100 rounded-lg transition cursor-pointer"
              >
                <RxCross1 size={22} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <p className="text-sm font-medium text-blue-700 mb-1">Total Sales (customer paid)</p>
                  <p className="text-2xl font-bold text-blue-900">£{selectedDay.totalSales.toFixed(2)}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <p className="text-sm font-medium text-green-700 mb-1">Valid Orders</p>
                  <p className="text-2xl font-bold text-green-900">{selectedDay.orderCount}</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                  <p className="text-sm font-medium text-purple-700 mb-1">Avg Order Value</p>
                  <p className="text-2xl font-bold text-purple-900">
                    £{selectedDay.orderCount > 0 ? (selectedDay.totalSales / selectedDay.orderCount).toFixed(2) : "0.00"}
                  </p>
                </div>
                <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                  <p className="text-sm font-medium text-amber-700 mb-1">Products Sold</p>
                  <p className="text-2xl font-bold text-amber-900">
                    {selectedDay.totalItems || 0}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                  <p className="text-xs font-medium text-emerald-800">Buyer protection (VA)</p>
                  <p className="text-lg font-bold text-emerald-900 mt-1">
                    £{Number(selectedDay.totalBuyerProtection || 0).toFixed(2)}
                  </p>
                </div>
                <div className="rounded-lg border border-teal-200 bg-teal-50 p-3">
                  <p className="text-xs font-medium text-teal-800">
                    VA seller fee ({platformFeePercent}%)
                  </p>
                  <p className="text-lg font-bold text-teal-900 mt-1">
                    £{Number(selectedDay.totalSellerPlatformFees || 0).toFixed(2)}
                  </p>
                </div>
                <div className="rounded-lg border border-purple-200 bg-purple-50 p-3">
                  <p className="text-xs font-medium text-purple-800">Featured listing (VA)</p>
                  <p className="text-lg font-bold text-purple-900 mt-1">
                    £{Number(selectedDay.totalFeaturedFees || 0).toFixed(2)}
                  </p>
                </div>
                <div className="rounded-lg border border-slate-300 bg-slate-50 p-3">
                  <p className="text-xs font-medium text-slate-700">VA net earnings (est.)</p>
                  <p className="text-lg font-bold text-slate-900 mt-1">
                    £{Number(selectedDay.totalVaNet || 0).toFixed(2)}
                  </p>
                </div>
              </div>

              {/* Orders List */}
              <div>
                <h4 className="text-lg font-semibold text-slate-900 mb-4">
                  Orders ({selectedDay.orders?.length || 0})
                  {dayOrdersLoading ? (
                    <span className="ml-2 text-sm font-normal text-slate-500">Loading…</span>
                  ) : null}
                </h4>
                <div className="space-y-3">
                  {dayOrdersLoading ? (
                    <div className="flex justify-center py-12">
                      <Loader />
                    </div>
                  ) : selectedDay.orders && selectedDay.orders.length > 0 ? (
                    selectedDay.orders.map((order, index) => {
                      const status = order.status || "Processing";
                      const statusLower = status.toLowerCase();
                      const isRefunded = statusLower.includes("refund") || statusLower === "refunded";
                      const isCancelled = statusLower.includes("cancel");
                      const statusColors = {
                        "Delivered": "bg-green-100 text-green-800",
                        "Processing": "bg-blue-100 text-blue-800",
                        "Shipping": "bg-yellow-100 text-yellow-800",
                        "On the way": "bg-orange-100 text-orange-800",
                        "Received": "bg-indigo-100 text-indigo-800",
                        "Transferred to delivery partner": "bg-purple-100 text-purple-800",
                      };
                      const statusClass = isRefunded 
                        ? "bg-red-100 text-red-800" 
                        : isCancelled 
                        ? "bg-gray-100 text-gray-800"
                        : statusColors[status] || "bg-gray-100 text-gray-800";
                      
                      const subTotal = Number(order.subTotalPrice ?? 0);
                      const shipping = Number(order.shipping ?? 0);
                      const buyerProtection = Number(order.buyerProtectionFee ?? 0);
                      const discount = Number(order.discountPrice ?? 0);
                      const total = Number(order.totalPrice ?? 0);
                      const va =
                        order.vaBreakdown || computeOrderVaBreakdown(order, feeSettings);
                      const sellerPlatformFee = Number(va.platformFeeAmount ?? 0);
                      const featuredFee = Number(va.featuredItemFee ?? 0);
                      const vaNet = Number(va.vaOrderEarnings ?? 0);

                      return (
                        <div key={order._id || index} className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition">
                          <div className="flex flex-col gap-3">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3 mb-2 flex-wrap">
                                  <span className="text-sm font-mono text-slate-600">
                                    Order ID: {order?.orderNumber ?? order?._id?.toString().substring(0, 8) ?? "N/A"}
                                  </span>
                                  <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${statusClass}`}>
                                    {status}
                                  </span>
                                  {isRefunded && (
                                    <span className="text-xs text-red-600 font-medium">(Refunded)</span>
                                  )}
                                </div>
                                <p className="text-sm text-slate-600 mb-1">
                                  Customer: <span className="font-medium text-slate-900">{order.user?.name || "N/A"}</span>
                                </p>
                                <p className="text-sm text-slate-600 mb-1">
                                  Order Date: <span className="font-medium text-slate-900">
                                    {order.createdAt ? new Date(order.createdAt).toLocaleString() : "N/A"}
                                  </span>
                                </p>
                              </div>
                              <div className="text-right shrink-0">
                                <p className={`text-lg font-bold ${isRefunded ? "text-red-600 line-through" : "text-slate-900"}`}>
                                  £{total.toFixed(2)}
                                </p>
                                {isRefunded && (
                                  <p className="text-xs text-red-600 mt-1">Refunded</p>
                                )}
                              </div>
                            </div>

                            {order.cart && order.cart.length > 0 && (
                              <div className="rounded border border-slate-200 bg-slate-50 p-3">
                                <p className="text-xs font-semibold text-slate-600 mb-2">Items</p>
                                <ul className="space-y-1.5 text-sm mb-3">
                                  {order.cart.map((item, i) => (
                                    <li key={item._id || i} className="flex justify-between gap-2 text-slate-700">
                                      <span className="truncate flex-1">
                                        {item.name || "Product"} × {Number(item.qty) || 0}
                                      </span>
                                      <span className="font-medium text-slate-900 shrink-0">
                                        £{((Number(item.qty) || 0) * Number(item.discountPrice || item.price || 0)).toFixed(2)}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                                <div className="border-t border-slate-200 pt-2 space-y-1 text-sm">
                                  <div className="flex justify-between text-slate-600">
                                    <span>Subtotal (products)</span>
                                    <span>£{subTotal.toFixed(2)}</span>
                                  </div>
                                  {shipping > 0 && (
                                    <div className="flex justify-between text-slate-600">
                                      <span>Delivery</span>
                                      <span>£{shipping.toFixed(2)}</span>
                                    </div>
                                  )}
                                  {buyerProtection > 0 && (
                                    <div className="flex justify-between text-emerald-700">
                                      <span>Buyer protection (VA)</span>
                                      <span>£{buyerProtection.toFixed(2)}</span>
                                    </div>
                                  )}
                                  {sellerPlatformFee > 0 && (
                                    <div className="flex justify-between text-teal-700">
                                      <span>VA seller fee ({platformFeePercent}% of items)</span>
                                      <span>£{sellerPlatformFee.toFixed(2)}</span>
                                    </div>
                                  )}
                                  {featuredFee > 0 && (
                                    <div className="flex justify-between text-purple-700">
                                      <span>Featured listing (VA)</span>
                                      <span>£{featuredFee.toFixed(2)}</span>
                                    </div>
                                  )}
                                  {(vaNet > 0 || sellerPlatformFee > 0 || buyerProtection > 0) && (
                                    <div className="flex justify-between text-slate-800 font-medium border-t border-dashed border-slate-300 pt-1 mt-1">
                                      <span>VA net earnings (est.)</span>
                                      <span>£{vaNet.toFixed(2)}</span>
                                    </div>
                                  )}
                                  {discount > 0 && (
                                    <div className="flex justify-between text-emerald-600">
                                      <span>Discount</span>
                                      <span>-£{discount.toFixed(2)}</span>
                                    </div>
                                  )}
                                  <div className="flex justify-between font-semibold text-slate-900 pt-1 border-t border-slate-200 mt-1">
                                    <span>Total (customer paid)</span>
                                    <span>£{total.toFixed(2)}</span>
                                  </div>
                                </div>
                              </div>
                            )}

                            {order.updatedAt && order.updatedAt !== order.createdAt && (
                              <p className="text-xs text-slate-500">
                                Last Updated: {new Date(order.updatedAt).toLocaleString()}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-slate-500 text-center py-8">No orders found for this date</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Escrow Details Modal */}
      {isEscrowDetailsOpen && selectedEscrowRow && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsEscrowDetailsOpen(false);
          }}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-[95%] max-w-3xl max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
              <h3 className="text-xl font-semibold text-slate-900">Escrow Order Details</h3>
              <button
                type="button"
                onClick={() => setIsEscrowDetailsOpen(false)}
                className="p-2 hover:bg-slate-100 rounded-lg transition cursor-pointer"
              >
                ✕
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-slate-600">Order</p>
                  <p className="text-sm text-slate-900">
                    {selectedEscrowRow.orderId ? `#${selectedEscrowRow.orderId}` : "—"}
                  </p>
                  <p className="text-sm text-slate-600">
                    Date:{" "}
                    <span className="font-medium text-slate-900">
                      {selectedEscrowRow.createdAt
                        ? new Date(selectedEscrowRow.createdAt).toLocaleDateString("en-GB")
                        : "—"}
                    </span>
                  </p>
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-medium text-slate-600">Amount</p>
                  <p className="text-lg font-semibold text-slate-900">
                    £{Number(selectedEscrowRow.amount || 0).toFixed(2)}
                  </p>
                  <p className="text-sm text-slate-600">
                    Payment status:{" "}
                    <span className="font-medium text-slate-900">
                      {selectedEscrowRow.paymentStatus || "—"}
                    </span>
                  </p>
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-medium text-slate-600">Seller</p>
                  <p className="text-sm font-semibold text-slate-900">{selectedEscrowRow.sellerName || "—"}</p>
                  <p className="text-sm text-slate-600">
                    Email:{" "}
                    <span className="font-medium text-slate-900">
                      {selectedEscrowRow.sellerEmail || "—"}
                    </span>
                  </p>
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-medium text-slate-600">Customer</p>
                  <p className="text-sm font-semibold text-slate-900">{selectedEscrowRow.customerName || "—"}</p>
                  <p className="text-sm text-slate-600">
                    Email:{" "}
                    <span className="font-medium text-slate-900">
                      {selectedEscrowRow.customerEmail || "—"}
                    </span>
                  </p>
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                This order has been paid successfully but payout has not been released yet.
              </div>
            </div>
          </div>
        </div>
      )}

      <RevenueDetailModal
        open={Boolean(metricModalId)}
        title={METRIC_MODAL_TITLES[metricModalId] || "Details"}
        onClose={closeMetricModal}
      >
        {metricModalId ? renderMetricModalBody(metricModalId, metricModalCtx) : null}
      </RevenueDetailModal>

      <TransactionDetailModal
        open={Boolean(selectedTransaction)}
        transaction={selectedTransaction}
        onClose={() => setSelectedTransaction(null)}
      />

      {selectedCategoryDetails ? (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeCategoryDetails();
          }}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-4">
              <div>
                <h3 className="text-xl font-semibold text-slate-900">
                  {selectedCategoryDetails.category}
                </h3>
                <p className="text-sm text-slate-600 mt-1">
                  {selectedCategoryDetails.itemsSold} item(s) sold · £
                  {Number(selectedCategoryDetails.salesAmount || 0).toFixed(2)}
                </p>
              </div>
              <button
                type="button"
                onClick={closeCategoryDetails}
                className="p-2 rounded-lg hover:bg-slate-100"
                aria-label="Close category details"
              >
                <RxCross1 size={22} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto space-y-6">
              {categoryDetailsLoading ? (
                <div className="py-12 flex justify-center">
                  <Loader />
                </div>
              ) : (
                <>
                  <div>
                    <h4 className="text-sm font-semibold text-slate-900 mb-2">
                      Products in this category
                    </h4>
                    {!selectedCategoryDetails.itemBreakdown?.length ? (
                      <p className="text-sm text-slate-500">No products found in this period.</p>
                    ) : (
                      <div className="overflow-x-auto rounded-lg border border-slate-200">
                        <table className="min-w-full text-sm">
                          <thead className="bg-slate-50">
                            <tr>
                              <th className="px-3 py-2 text-left font-semibold text-slate-700">
                                Product
                              </th>
                              <th className="px-3 py-2 text-left font-semibold text-slate-700">
                                Qty sold
                              </th>
                              <th className="px-3 py-2 text-left font-semibold text-slate-700">
                                Sales amount
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedCategoryDetails.itemBreakdown.map((item) => (
                              <tr key={item.name} className="border-t border-slate-200">
                                <td className="px-3 py-2 text-slate-800">{item.name}</td>
                                <td className="px-3 py-2 text-slate-600">{item.quantity}</td>
                                <td className="px-3 py-2 text-slate-600">
                                  £{Number(item.salesAmount || 0).toFixed(2)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-slate-900 mb-2">
                      Order line items
                    </h4>
                    {!selectedCategoryDetails.lineItems?.length ? (
                      <p className="text-sm text-slate-500">No order lines in this period.</p>
                    ) : (
                      <div className="overflow-x-auto max-h-[45vh] rounded-lg border border-slate-200">
                        <table className="min-w-full text-sm">
                          <thead className="bg-slate-50 sticky top-0">
                            <tr>
                              <th className="px-3 py-2 text-left font-semibold text-slate-700">
                                Date
                              </th>
                              <th className="px-3 py-2 text-left font-semibold text-slate-700">
                                Order
                              </th>
                              <th className="px-3 py-2 text-left font-semibold text-slate-700">
                                Product
                              </th>
                              <th className="px-3 py-2 text-left font-semibold text-slate-700">
                                Qty
                              </th>
                              <th className="px-3 py-2 text-left font-semibold text-slate-700">
                                Line total
                              </th>
                              <th className="px-3 py-2 text-left font-semibold text-slate-700">
                                Buyer
                              </th>
                              <th className="px-3 py-2 text-left font-semibold text-slate-700">
                                Status
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedCategoryDetails.lineItems.map((line) => (
                              <tr key={line.id} className="border-t border-slate-200">
                                <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
                                  {formatTxDate(line.date)}
                                </td>
                                <td className="px-3 py-2">
                                  {line.orderId ? (
                                    <Link
                                      to={`/admin-order/${line.orderId}`}
                                      className="text-[#38513B] font-medium hover:underline"
                                    >
                                      {line.reference}
                                    </Link>
                                  ) : (
                                    line.reference
                                  )}
                                </td>
                                <td className="px-3 py-2 text-slate-800">{line.productName}</td>
                                <td className="px-3 py-2 text-slate-600">{line.qty}</td>
                                <td className="px-3 py-2 text-slate-600">
                                  £{Number(line.lineTotal || 0).toFixed(2)}
                                </td>
                                <td className="px-3 py-2 text-slate-600">{line.buyerName}</td>
                                <td className="px-3 py-2 text-slate-600">{line.orderStatus}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AdminDashboardTotalSales;
